// Global branded error modal (#7095).
//
// One modal instance is rendered hidden in the layout
// (app/views/shared/_global_error_modal.html.haml) so the whitelabel-aware
// brand logo arrives server-rendered. This module decides WHEN to show it:
//
//   - `turbo:fetch-request-error` — network-level failure of any Turbo-managed
//     request (Drive visit, frame, stream form). Fires when the server is
//     unreachable, so this is the only signal we will ever get for that case.
//   - `turbo:frame-missing` with a 5xx/408 response — a frame request hit a
//     server error. The page is still alive, so a modal beats the generic
//     fallback of visiting the broken URL (see application.js).
//   - `goat:server-error` CustomEvent — dispatched by the React API layer
//     (app/frontend/react/lib/api.ts) for any 5xx/408/network failure. A 503
//     whose detail carries `code: 'maintenance_window'` (#13642) is a planned
//     wait, not a failure: the modal shows the server's "try again in N
//     minutes" sentence under a maintenance title and drops the "team has
//     been notified" line — nobody needs notifying.
//
// Permission denials (a 403 with code `permission_denied`) are NOT errors and
// get their own styled "Permission needed" modal — see ./permission_denied.js,
// whose listener is registered from initGlobalErrorModal so both backstops
// ship together (#12888).
//
// Full-page Turbo Drive navigations that 5xx are intentionally NOT handled
// here: Turbo renders the error response document, which is the matching
// branded public/500.html.

import {
  classifySessionAfterNetworkFailure,
  isSessionExpiryRedirecting,
  redirectToLoginForExpiredSession,
  watchSessionRecoveryAfterOutage,
  SESSION_PROBE,
} from './session_expiry'
import { initPermissionDeniedListener } from './permission_denied'

const MODAL_ID = 'globalErrorModal'
const DETAIL_ID = 'globalErrorModalDetail'
const TITLE_TEXT_ID = 'globalErrorModalTitleText'
const COPY_ID = 'globalErrorModalCopy'
const TRY_AGAIN_ID = 'global-error-try-again'

// Mirrors MaintenanceNotice::MAINTENANCE_WINDOW_CODE / api.ts MAINTENANCE_WINDOW_CODE.
export const MAINTENANCE_WINDOW_CODE = 'maintenance_window'
export const DEFAULT_TITLE = 'Something went wrong'
export const MAINTENANCE_TITLE = 'Brief maintenance'

let modalOpen = false

export function isServerErrorStatus(status) {
  return status >= 500 || status === 408
}

// Shows the modal (at most one at a time — repeated failures while it is open,
// e.g. a TanStack Query retry, must not stack a second dialog). Returns true
// if the modal was shown. `detailMessage` is an optional server-provided hint
// (e.g. the 408 "narrow your filters" guidance) rendered under the main copy.
// `maintenance: true` (#13642) swaps the title for the maintenance one and
// hides the "team has been notified" copy, so the detail — the server's
// "try again in about N minutes" sentence — is the whole message.
export function showGlobalErrorModal(detailMessage = null, { maintenance = false } = {}) {
  if (modalOpen) return false
  // An expired session is not an error — we're already redirecting to login,
  // so suppress the "Something went wrong" modal.
  if (isSessionExpiryRedirecting()) return false
  const modalEl = document.getElementById(MODAL_ID)
  if (!modalEl || !window.bootstrap) return false

  const titleEl = document.getElementById(TITLE_TEXT_ID)
  if (titleEl) titleEl.textContent = maintenance ? MAINTENANCE_TITLE : DEFAULT_TITLE
  const copyEl = document.getElementById(COPY_ID)
  if (copyEl) copyEl.classList.toggle('d-none', maintenance)
  const detailEl = document.getElementById(DETAIL_ID)
  if (detailEl) {
    detailEl.textContent = detailMessage || ''
    detailEl.classList.toggle('d-none', !detailMessage)
  }

  modalOpen = true
  modalEl.addEventListener('hidden.bs.modal', () => { modalOpen = false }, { once: true })
  window.bootstrap.Modal.getOrCreateInstance(modalEl).show()
  return true
}

// Called from the application.js `turbo:frame-missing` handler. Returns true
// when the missing frame was caused by a server error and the modal handled
// it, so the caller skips its visit-the-response-URL fallback (which would
// re-request a broken endpoint as a GET and dump the user on the crash page).
export function frameMissingDueToServerError(response) {
  if (!response || !isServerErrorStatus(response.status)) return false
  showGlobalErrorModal()
  return true
}

// A network-level failure (the request never received an HTTP response) is
// ambiguous: an expired session on wake-from-sleep, a transient blip while the
// network stack comes back up, or a genuine outage. Waking a laptop overnight
// is by far the most common cause, and the right response there is the login
// page, not "Something went wrong" — so probe the session before deciding.
// Session expired → login redirect. Session alive → transient blip; suppress
// the modal (callers' inline error UI and query retries cover it).
//
// Probe also unreachable → the session state is still UNKNOWN, and most of the
// time nothing is wrong (the network just isn't back yet, and the session has
// usually timed out overnight) — so stay SILENT and keep watching until it
// settles: expired → login page, alive → leave the page alone. Only a watch
// that exhausts every attempt (a sustained, confirmed outage) shows the modal
// (owner directive 2026-08-10: a timeout must never surface as an error).
let networkFailureProbe = null

function handleNetworkLevelFailure() {
  if (modalOpen || isSessionExpiryRedirecting()) return
  if (networkFailureProbe) return

  networkFailureProbe = classifySessionAfterNetworkFailure().then((verdict) => {
    networkFailureProbe = null
    if (verdict === SESSION_PROBE.UNAUTHENTICATED) {
      redirectToLoginForExpiredSession()
    } else if (verdict === SESSION_PROBE.UNREACHABLE) {
      // Returns null when a watch is already running (query retries during the
      // same outage) — that watch already owns the verdict; don't stack.
      watchSessionRecoveryAfterOutage()?.then((watchVerdict) => {
        if (watchVerdict === SESSION_PROBE.UNREACHABLE) showGlobalErrorModal()
      })
    }
  })
}

function handleFetchRequestError(event) {
  const { request, error } = event.detail || {}
  // Cancelled requests (user navigated away mid-flight) are not failures.
  if (error?.name === 'AbortError') return
  // Turbo link prefetches fire on hover with no user-visible action — a flaky
  // prefetch must not pop a modal. Turbo marks them with X-Sec-Purpose.
  if (request?.headers?.['X-Sec-Purpose'] === 'prefetch') return
  handleNetworkLevelFailure()
}

function handleReactServerError(event) {
  // Status 0 = network-level failure signaled by the React API layer
  // (app/frontend/react/lib/api.ts signalNetworkError) — ambiguous, probe.
  if (event.detail?.status === 0) {
    handleNetworkLevelFailure()
    return
  }
  const message = event.detail?.message || null
  const maintenance = Boolean(message) && event.detail?.code === MAINTENANCE_WINDOW_CODE
  showGlobalErrorModal(message, { maintenance })
}

// Indirection for the hard reload — jsdom's window.location is unforgeable,
// so tests stub this object instead.
export const navigation = {
  reload: () => window.location.reload(),
}

function handleTryAgainClick(event) {
  if (!event.target.closest(`#${TRY_AGAIN_ID}`)) return
  navigation.reload()
}

// A Turbo Drive navigation replaces <body> (and the modal element) without
// Bootstrap ever firing hidden.bs.modal on the removed node — without this
// reset, modalOpen would stay true forever and silently suppress every
// subsequent error modal.
function handleVisit() {
  modalOpen = false
}

let initialized = false

export function initGlobalErrorModal() {
  if (initialized) return
  initialized = true
  document.addEventListener('turbo:fetch-request-error', handleFetchRequestError)
  document.addEventListener('goat:server-error', handleReactServerError)
  initPermissionDeniedListener()
  document.addEventListener('turbo:visit', handleVisit)
  document.addEventListener('click', handleTryAgainClick)
  // Ad-hoc callers (Stimulus controllers using @rails/request.js, which does
  // not emit Turbo events) can opt in without importing the module.
  window.showGlobalErrorModal = showGlobalErrorModal
}
